/**
 * Canvas-space hit testing for pitch elements.
 *
 * All coordinates and thresholds are in CSS pixels, so interaction
 * feels consistent regardless of world-coordinate scale or canvas size.
 */

export interface HitTestPoint {
  id: string
  cx: number
  cy: number
}

export interface HitTestArrow {
  id: string
  /** Start position in canvas pixels */
  fromCx: number
  fromCy: number
  /** End position in canvas pixels */
  toCx: number
  toCy: number
}

export interface HitTestOptions {
  /** Pixel radius around a point center that counts as a hit (default 14). */
  pointRadius?: number
  /** Pixel distance from an arrow line segment that counts as a hit (default 10). */
  lineTolerance?: number
}

export interface HitTestResult {
  id: string
  type: 'point' | 'arrow'
  /** Pixel distance from the click to the element (0 = dead center). */
  distance: number
}

const DEFAULT_POINT_RADIUS = 14
const DEFAULT_LINE_TOLERANCE = 10

/**
 * Squared distance between two points.
 */
function distSq(ax: number, ay: number, bx: number, by: number): number {
  return (ax - bx) ** 2 + (ay - by) ** 2
}

/**
 * Squared distance from point (px, py) to the nearest point on
 * line segment (x1,y1)→(x2,y2).
 *
 * Projects the point onto the infinite line, clamps the parameter
 * to [0,1] so we stay within the segment, then returns the squared
 * distance to that clamped projection.
 */
export function distToSegmentSq(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1
  const dy = y2 - y1
  const lenSq = dx * dx + dy * dy

  // Degenerate segment (zero length) — treat as a point
  if (lenSq === 0) return distSq(px, py, x1, y1)

  // Parameter t of the projection onto the infinite line, clamped to [0,1]
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq))

  // Nearest point on the segment
  const projX = x1 + t * dx
  const projY = y1 + t * dy

  return distSq(px, py, projX, projY)
}

/**
 * Find the nearest interactive element to a click position.
 *
 * Tests points (circle hit area) and arrows (line-segment proximity)
 * in canvas pixel space. Returns the closest element within threshold,
 * or null if nothing was hit.
 *
 * Points take priority over arrows at equal distances because they're
 * smaller targets and harder to click precisely.
 */
export function hitTestElements(
  points: HitTestPoint[],
  arrows: HitTestArrow[],
  clickCx: number,
  clickCy: number,
  options?: HitTestOptions
): HitTestResult | null {
  const pointRadius = options?.pointRadius ?? DEFAULT_POINT_RADIUS
  const lineTolerance = options?.lineTolerance ?? DEFAULT_LINE_TOLERANCE

  const pointRadiusSq = pointRadius * pointRadius
  const lineToleranceSq = lineTolerance * lineTolerance

  let best: HitTestResult | null = null
  let bestDistSq = Infinity

  // Test points first — they get priority at equal distance
  for (const pt of points) {
    const dSq = distSq(clickCx, clickCy, pt.cx, pt.cy)
    if (dSq <= pointRadiusSq && dSq < bestDistSq) {
      bestDistSq = dSq
      best = { id: pt.id, type: 'point', distance: Math.sqrt(dSq) }
    }
  }

  // Test arrows — distance to line segment
  for (const arrow of arrows) {
    const dSq = distToSegmentSq(
      clickCx,
      clickCy,
      arrow.fromCx,
      arrow.fromCy,
      arrow.toCx,
      arrow.toCy
    )
    if (dSq <= lineToleranceSq && dSq < bestDistSq) {
      bestDistSq = dSq
      best = { id: arrow.id, type: 'arrow', distance: Math.sqrt(dSq) }
    }
  }

  return best
}
